/* eslint-disable react-hooks/set-state-in-effect */
import { useState, useMemo, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Camera, Plus, Download, Eye } from 'lucide-react';
import { Button, Badge } from '@/components/v2/ui';
import { type ImageOptimizeState } from './useImageOptimizeState';
import { type Dispatch, type SetStateAction } from 'react';

interface Props {
  state: ImageOptimizeState;
  setState: Dispatch<SetStateAction<ImageOptimizeState>>;
  reset: () => void;
}

export function ImageOptimizeStage({ state, setState, reset }: Props) {
  const { t } = useTranslation();
  const [showOriginal, setShowOriginal] = useState(false);
  const displayUrl = showOriginal ? state.currentImageUrl : (state.editedImageUrl ?? state.currentImageUrl);

  const [dimensions, setDimensions] = useState<{ w: number; h: number } | null>(null);

  useEffect(() => {
    if (!state.currentImageUrl) { setDimensions(null); return; }
    const img = new window.Image();
    img.onload = () => setDimensions({ w: img.naturalWidth, h: img.naturalHeight });
    img.src = state.currentImageUrl;
  }, [state.currentImageUrl]);

  const fileMeta = useMemo(() => {
    if (!state.currentFile) return null;
    const sizeMB = (state.currentFile.size / (1024 * 1024)).toFixed(1);
    const ext = state.currentFile.name.split('.').pop()?.toUpperCase() ?? '';
    return { sizeMB, ext, dimensions };
  }, [state.currentFile, dimensions]);

  const handleDownload = useCallback(() => {
    if (!displayUrl) return;
    const a = document.createElement('a');
    a.href = displayUrl;
    a.download = state.currentFile?.name ?? 'optimized-image';
    a.click();
  }, [displayUrl, state.currentFile]);

  if (!state.currentFile) {
    return (
      <div className="flex items-center justify-center h-full bg-neutral-100">
        <div className="border-2 border-dashed border-neutral-300 rounded-xl p-12 text-center">
          <Camera className="mx-auto h-12 w-12 text-neutral-400 mb-4" />
          <p className="text-neutral-600 font-medium">{t('imageOptimize.empty.title')}</p>
          <p className="text-sm text-neutral-400 mt-1">{t('imageOptimize.empty.subtitle')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full bg-neutral-100">
      <StageTopBar fileMeta={fileMeta} hasEdited={!!state.editedImageUrl} showOriginal={showOriginal}
        onToggleOriginal={() => setShowOriginal(v => !v)} onReset={reset} onDownload={handleDownload} />
      <div className="flex-1 flex items-center justify-center p-4 overflow-hidden">
        <div className="relative">
          {state.editedImageUrl && !showOriginal && (
            <Badge variant="success" className="absolute top-2 left-2 z-10">{t('imageOptimize.imageEdited')}</Badge>
          )}
          <img src={displayUrl!} alt={state.currentFile?.name ?? ''}
            className="max-w-[500px] max-h-[500px] object-contain rounded-lg"
            style={{ background: 'repeating-conic-gradient(#d4d4d4 0% 25%, transparent 0% 50%) 50% / 20px 20px' }} />
        </div>
      </div>
      {state.editHistory.length > 0 && (
        <div className="flex items-center gap-2 px-4 py-2 bg-white border-t border-neutral-200 overflow-x-auto">
          <ThumbBtn url={state.currentImageUrl!} active={!state.editedImageUrl || showOriginal}
            label={t('imageOptimize.original')} onClick={() => { setShowOriginal(true); setState(p => ({ ...p, editedS3Key: null })); }} />
          {state.editHistory.map((e, i) => (
            <ThumbBtn key={i} url={e.imageUrl} active={state.editedImageUrl === e.imageUrl && !showOriginal}
              label={t(e.labelKey)} onClick={() => { setShowOriginal(false); setState(p => ({ ...p, editedImageUrl: e.imageUrl, editedS3Key: e.s3Key })); }} />
          ))}
        </div>
      )}
    </div>
  );
}

function ThumbBtn({ url, active, label, onClick }: {
  url: string; active: boolean; label: string; onClick: () => void;
}) {
  return (
    <button onClick={onClick} title={label}
      className={`flex-shrink-0 rounded border-2 transition-colors ${active ? 'border-orange-500' : 'border-transparent hover:border-neutral-300'}`}>
      <img src={url} alt={label} className="w-14 h-14 object-cover rounded" />
    </button>
  );
}

function StageTopBar({ fileMeta, hasEdited, onToggleOriginal, onReset, onDownload }: {
  fileMeta: { sizeMB: string; ext: string; dimensions: { w: number; h: number } | null } | null;
  hasEdited: boolean; showOriginal: boolean;
  onToggleOriginal: () => void; onReset: () => void; onDownload: () => void;
}) {
  const { t } = useTranslation();
  return (
    <div className="flex items-center justify-between px-4 py-2 bg-white border-b border-neutral-200">
      <div className="text-xs text-neutral-500">
        {fileMeta && (
          <span>
            {fileMeta.ext} &middot; {fileMeta.sizeMB} MB
            {fileMeta.dimensions && <> &middot; {fileMeta.dimensions.w} &times; {fileMeta.dimensions.h} px</>}
          </span>
        )}
      </div>
      <div className="flex gap-2">
        {hasEdited && (
          <Button variant="ghost" size="sm" iconLeft={<Eye className="h-4 w-4" />} onClick={onToggleOriginal}>
            {t('imageOptimize.showOriginal')}
          </Button>
        )}
        <Button variant="ghost" size="sm" iconLeft={<Plus className="h-4 w-4" />} onClick={onReset}>
          {t('imageOptimize.buttonNew')}
        </Button>
        <Button variant="outline" size="sm" iconLeft={<Download className="h-4 w-4" />} onClick={onDownload}>
          {t('imageOptimize.buttonDownload')}
        </Button>
      </div>
    </div>
  );
}
